import React, { useEffect, useMemo, useState } from 'react';
import hrApi from '../../api';
import { formatDisplayName } from '../../utils/formatTime';

const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5];
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
];

const emptyForm = {
  name: '',
  startDate: '',
  endDate: '',
};

function toDateInput(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeRule(rule) {
  const workingDaysRaw = Array.isArray(rule?.workingDays) ? rule.workingDays : DEFAULT_WORKING_DAYS;
  const workingDays = [...new Set(workingDaysRaw.map((d) => Number(d)).filter((d) => d >= 1 && d <= 5))].sort((a, b) => a - b);
  return {
    workingDays: workingDays.length ? workingDays : [...DEFAULT_WORKING_DAYS],
  };
}

function workingDaysLabel(workingDays) {
  const selected = Array.isArray(workingDays) ? workingDays : [];
  if (selected.length === 5) return 'Full week';
  if (selected.length === 0) return 'No days selected';
  const labels = WEEKDAY_OPTIONS.filter((d) => selected.includes(d.value)).map((d) => d.label);
  return labels.join(', ');
}

function employeeCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} employee${n === 1 ? '' : 's'}`;
}

export default function WorkScheduleOverrides({ employees = [] }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedAdminIds, setSelectedAdminIds] = useState([]);
  const [rulesByAdminId, setRulesByAdminId] = useState({});
  const [validationError, setValidationError] = useState('');

  const availableEmployees = useMemo(
    () => (employees || []).filter((emp) => String(emp.role || '').trim() !== 'manager'),
    [employees]
  );

  const employeeNameById = useMemo(() => {
    const map = {};
    availableEmployees.forEach((emp) => {
      map[String(emp._id)] = formatDisplayName(emp.username || emp.email || String(emp._id));
    });
    return map;
  }, [availableEmployees]);

  const loadList = () => {
    setLoading(true);
    setError('');
    hrApi.get('/api/work-schedule-overrides')
      .then((res) => setList(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setError('Failed to load event overrides');
        setList([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList();
  }, []);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setSelectedAdminIds([]);
    setRulesByAdminId({});
    setValidationError('');
    setSaving(false);
  };

  const toggleEmployee = (adminId, checked) => {
    const id = String(adminId);
    setSelectedAdminIds((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
    if (checked) {
      setRulesByAdminId((prev) => ({
        ...prev,
        [id]: prev[id] || { workingDays: [...DEFAULT_WORKING_DAYS] },
      }));
    } else {
      setRulesByAdminId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedAdminIds([]);
    setRulesByAdminId({});
    setEditingId(null);
    setValidationError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    const rules = Array.isArray(item.employeeRules) ? item.employeeRules : [];
    const allowedIds = new Set(availableEmployees.map((e) => String(e._id)));
    const selected = rules
      .map((r) => String(r.adminId))
      .filter((id) => allowedIds.has(id));
    const mapped = {};
    rules.forEach((r) => {
      const id = String(r.adminId);
      if (!allowedIds.has(id)) return;
      mapped[id] = normalizeRule(r);
    });
    setEditingId(item._id);
    setForm({
      name: item.name || '',
      startDate: toDateInput(item.startDate),
      endDate: toDateInput(item.endDate),
    });
    setSelectedAdminIds(selected);
    setRulesByAdminId(mapped);
    setValidationError('');
    setShowForm(true);
  };

  const toggleWorkingDay = (adminId, weekday, checked) => {
    const id = String(adminId);
    const current = normalizeRule(rulesByAdminId[id]);
    const set = new Set(current.workingDays || DEFAULT_WORKING_DAYS);
    if (checked) set.add(weekday);
    else set.delete(weekday);
    const nextWorkingDays = [...set].sort((a, b) => a - b);
    setRulesByAdminId((prev) => ({
      ...prev,
      [id]: {
        workingDays: nextWorkingDays,
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setValidationError('');
    const name = String(form.name || '').trim();
    if (!name || !form.startDate || !form.endDate) return;
    if (form.endDate < form.startDate) {
      setValidationError('End date must be on or after start date.');
      return;
    }
    if (selectedAdminIds.length === 0) {
      setValidationError('Select at least one employee.');
      return;
    }
    const employeeRules = selectedAdminIds.map((adminId) => {
      const normalized = normalizeRule(rulesByAdminId[adminId]);
      const workingDays = normalized.workingDays || [];
      return {
        adminId,
        mode: workingDays.length === 5 ? 'full_time' : 'custom_days',
        hoursPerDay: 8,
        workingDays,
      };
    });
    if (employeeRules.some((r) => !Array.isArray(r.workingDays) || r.workingDays.length === 0)) {
      setValidationError('Every selected employee must have at least one weekday selected.');
      return;
    }
    setSaving(true);
    const payload = {
      name,
      startDate: form.startDate,
      endDate: form.endDate,
      employeeRules,
    };
    try {
      if (editingId) {
        await hrApi.put(`/api/work-schedule-overrides/${editingId}`, payload);
      } else {
        await hrApi.post('/api/work-schedule-overrides', payload);
      }
      resetForm();
      loadList();
    } catch (_) {
      setSaving(false);
      setError('Failed to save event override');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event override?')) return;
    try {
      await hrApi.delete(`/api/work-schedule-overrides/${id}`);
      loadList();
    } catch (_) {
      setError('Failed to delete event override');
    }
  };

  return (
    <section className="mt-4">
      <hr className="my-4" />
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <h5 className="mb-1">Event employee overrides</h5>
          <p className="small text-muted mb-0">Choose a date range and only the selected employees for temporary event schedules.</p>
        </div>
        {!showForm && (
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
            <i className="bi bi-calendar-plus me-1" />
            Add event override
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {showForm && (
        <form className="hr-section-card mb-3" onSubmit={handleSave}>
          <div className="row g-3 mb-3">
            <div className="col-lg-5">
              <label className="form-label small mb-1">Event name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Olympic Games 2026"
                required
              />
            </div>
            <div className="col-sm-6 col-lg-3">
              <label className="form-label small mb-1">Start date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.startDate}
                onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                required
              />
            </div>
            <div className="col-sm-6 col-lg-3">
              <label className="form-label small mb-1">End date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.endDate}
                onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <div className="small fw-medium mb-0">Employee schedule override</div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  const ids = availableEmployees.map((e) => String(e._id));
                  setSelectedAdminIds(ids);
                  const next = {};
                  ids.forEach((id) => { next[id] = { workingDays: [...DEFAULT_WORKING_DAYS] }; });
                  setRulesByAdminId(next);
                }}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSelectedAdminIds([]);
                  setRulesByAdminId({});
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="hr-allowance-table-wrap">
            <div className="table-responsive">
              <table className="table table-sm hr-allowance-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Use</th>
                    <th>Employee</th>
                    {WEEKDAY_OPTIONS.map((day) => (
                      <th key={day.value} className="text-center" style={{ width: 74 }}>{day.label}</th>
                    ))}
                    <th style={{ minWidth: 140 }}>Schedule</th>
                  </tr>
                </thead>
                <tbody>
                  {availableEmployees.map((emp) => {
                    const id = String(emp._id);
                    const selected = selectedAdminIds.includes(id);
                    const rule = normalizeRule(rulesByAdminId[id]);
                    return (
                      <tr key={id}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={selected}
                            onChange={(e) => toggleEmployee(id, e.target.checked)}
                          />
                        </td>
                        <td className="hr-allowance-name">{employeeNameById[id]}</td>
                        {WEEKDAY_OPTIONS.map((day) => (
                          <td key={day.value} className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={selected && (rule.workingDays || []).includes(day.value)}
                              disabled={!selected}
                              onChange={(e) => toggleWorkingDay(id, day.value, e.target.checked)}
                            />
                          </td>
                        ))}
                        <td className="small text-muted">
                          {selected ? workingDaysLabel(rule.workingDays) : 'Not selected'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {validationError && <p className="small text-danger mt-2 mb-0">{validationError}</p>}

          <div className="d-flex gap-2 mt-3">
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving || selectedAdminIds.length === 0}>
              {saving ? 'Saving...' : (editingId ? 'Update override' : 'Save override')}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="small text-muted mb-0">Loading event overrides...</p>
      ) : list.length === 0 ? (
        <p className="small text-muted mb-0">No event overrides configured.</p>
      ) : (
        <div className="hr-allowance-table-wrap">
          <div className="table-responsive">
            <table className="table table-sm hr-allowance-table mb-0">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Date range</th>
                  <th>Employees</th>
                  <th style={{ minWidth: 200 }}>Configured days</th>
                  <th style={{ width: 160 }} />
                </tr>
              </thead>
              <tbody>
                {list.map((item) => {
                  const rules = Array.isArray(item.employeeRules) ? item.employeeRules : [];
                  const previewRules = rules.slice(0, 2).map((rule) => {
                    const aid = String(rule.adminId || '');
                    const name = employeeNameById[aid] || 'Employee';
                    const normalized = normalizeRule(rule);
                    return `${name}: ${workingDaysLabel(normalized.workingDays)}`;
                  });
                  const restCount = Math.max(0, rules.length - previewRules.length);
                  return (
                    <tr key={item._id}>
                      <td className="hr-allowance-name">{item.name}</td>
                      <td>{toDateInput(item.startDate)} - {toDateInput(item.endDate)}</td>
                      <td>{employeeCountLabel(rules.length)}</td>
                      <td className="small">
                        {previewRules.map((text, i) => (
                          <div key={`${item._id}-${i}`}>{text}</div>
                        ))}
                        {restCount > 0 && (
                          <div className="text-muted">+{restCount} more</div>
                        )}
                      </td>
                      <td className="text-end">
                        <button type="button" className="btn btn-outline-primary btn-sm me-2" onClick={() => openEdit(item)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(item._id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
